import { createContext, useCallback, useContext, useMemo, useState, useEffect, type ReactNode } from 'react';
import {
  login as authLogin,
  definirNovaSenha as authDefinirNovaSenha,
  logout as authLogout,
  getCurrentUserInfo,
  isAuthenticated as checkAuth,
  type LoginCredentials,
  type UserInfo,
} from '@/services/auth.service';

export const GROUPS = {
  ADMINS: 'Admins',
  CLUBES: 'Clubes',
  CICLISTAS: 'Ciclistas',
} as const;

interface AuthContextData {
  user: UserInfo | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** `'nova-senha'`: primeiro login de conta convidada; conclua com `definirNovaSenha`. */
  login: (credentials: LoginCredentials) => Promise<'concluido' | 'nova-senha'>;
  definirNovaSenha: (novaSenha: string) => Promise<void>;
  logout: () => Promise<void>;
  isAdmin: () => boolean;
  isClubes: () => boolean;
  isCiclista: () => boolean;
  hasGroup: (group: string) => boolean;
  getClubeId: () => number | undefined;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

export function AuthProvider({ children }: Readonly<{ children: ReactNode }>) {
  const [user, setUser] = useState<UserInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const check = async () => {
      try {
        const authenticated = await checkAuth();
        if (authenticated) {
          const userInfo = await getCurrentUserInfo();
          setUser(userInfo);
        }
      } catch {
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };
    check();
  }, []);

  const login = useCallback(async (credentials: LoginCredentials) => {
    setIsLoading(true);
    try {
      const resultado = await authLogin(credentials);
      if (resultado.etapa === 'concluido') setUser(resultado.usuario);
      return resultado.etapa;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const definirNovaSenha = useCallback(async (novaSenha: string) => {
    setIsLoading(true);
    try {
      setUser(await authDefinirNovaSenha(novaSenha));
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    setIsLoading(true);
    try {
      await authLogout();
    } catch (err) {
      console.error('Erro no logout:', err);
    } finally {
      setUser(null);
      setIsLoading(false);
    }
  }, []);

  // Cada função estável por si: só assim o valor do contexto pode ser memoizado sem
  // dependência incompleta. Sem isso, o objeto era novo a cada render do provider e
  // cada consumidor re-renderizava junto — e este provider envolve a aplicação inteira.
  const hasGroup = useCallback((group: string) => user?.groups.includes(group) ?? false, [user]);
  const isAdmin = useCallback(() => hasGroup(GROUPS.ADMINS), [hasGroup]);
  const isClubes = useCallback(() => hasGroup(GROUPS.CLUBES), [hasGroup]);
  const isCiclista = useCallback(() => hasGroup(GROUPS.CICLISTAS), [hasGroup]);
  const getClubeId = useCallback(() => (isClubes() ? user?.idClube : undefined), [isClubes, user]);

  const valor = useMemo(
    () => ({ user, isAuthenticated: !!user, isLoading, login, definirNovaSenha, logout, isAdmin, isClubes, isCiclista, hasGroup, getClubeId }),
    [user, isLoading, login, definirNovaSenha, logout, isAdmin, isClubes, isCiclista, hasGroup, getClubeId],
  );

  return <AuthContext.Provider value={valor}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextData {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth deve ser usado dentro de AuthProvider');
  return ctx;
}
